'use client';

import { Button, IconButton, Stack, Typography, useBreakpoints } from '@castlery/fortress';
import { Check, Favorite, Loading, ShoppingBag } from '@castlery/fortress/Icons';
import { addToOrder, getProductBySKU } from '@castlery/modules-cms-services';
import { selectOrder, setOrder } from '@castlery/modules-order-domain';
import { toPrice } from '@castlery/modules-product-domain';
import {
  EVENT_ADD_TO_CART,
  EVENT_ADD_TO_WISHLIST,
  EVENT_STORYBLOK,
  getProductNeedTracking,
} from '@castlery/modules-tracking-services';
import {
  addWishlist,
  deleteWishlist,
  selectedWishList,
  setWishList,
  updateTempRemoveWishListItemInfo,
  updateWishlistActionRecord,
} from '@castlery/modules-user-domain';
import { CustomLink, FortressImage } from '@castlery/shared-components';
import { useAppDispatch, useAppSelector } from '@castlery/shared-redux-store';
import React, { useEffect, useMemo, useState } from 'react';
import Slider from 'react-slick';

interface ProductItemDataProps {
  sku: string;
  badges: string[];
  images: {
    base: string;
    lifestyle?: string;
  };
  inStock: boolean;
  name: string;
  price: number;
  salePrice: string;
  productShortDescription: string;
  spuName: string;
  url: string;
  variantId: string;
  slotId?: string;
  productType?: 'configurable' | 'bundle';
}

interface ProductItemProps {
  product: ProductItemDataProps;
  openHover?: boolean;
  imageType?: 'base_image' | 'lifestyle_image';
  bgColor?: string;
  needMargin?: boolean;
  length?: number;
  onModalOpen?: (value: boolean, type: 'success' | 'error', detail?: string) => void;
  onToastOpen?: (status: 'add' | 'remove' | 'temp_remove', info?: { id: number; name: string }) => void;
  applyATCAndWishlist?: boolean;
  onClickSuccess?: ({ slotId }: { slotId: string }) => void;
  inWishlistPage?: boolean;
}

const SingleProductItem = ({
  product,
  openHover = false,
  imageType,
  bgColor,
  length,
  needMargin,
  onModalOpen,
  onToastOpen,
  applyATCAndWishlist,
  onClickSuccess,
  inWishlistPage = false,
}: ProductItemProps) => {
  const {
    sku,
    badges,
    images,
    name,
    price,
    salePrice,
    productShortDescription,
    spuName,
    url,
    variantId,
    slotId,
    productType,
  } = product;
  const currentOrderData = useAppSelector(selectOrder);
  const wishList = useAppSelector(selectedWishList);
  const [isSelected, setIsSelected] = useState(false);
  const [waitingAddToCart, setWaitingAddToCart] = useState(false);
  const [addToCartSuccess, setAddToCartSuccess] = useState(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (currentOrderData?.line_items?.find((item) => item.variant?.id === Number(variantId))) {
      setAddToCartSuccess(true);
    } else {
      setAddToCartSuccess(false);
    }
  }, [currentOrderData, variantId]);

  useEffect(() => {
    if (wishList.find((item) => item.id === Number(variantId))) {
      setIsSelected(true);
    } else {
      setIsSelected(false);
    }
  }, [wishList, variantId]);

  const { base, lifestyle } = images;
  const { desktop } = useBreakpoints();

  const showLineThroughPrice = useMemo(() => {
    return !!salePrice;
  }, [salePrice]);

  const [isHover, setIsHover] = useState(false);

  const baseChangeColor = useMemo(() => {
    const replaceColor = bgColor || '#FBF9F4';
    const colorWithoutHash = replaceColor.replace('#', '');

    if (base && base.includes('b_rgb:')) {
      return base.replace(/b_rgb:[A-F0-9]{6},/, `b_rgb:${colorWithoutHash},`);
    }
    return base;
  }, [base, bgColor]);

  const handleMouseEnter = () => {
    setIsHover(true);
  };

  const handleMouseLeave = () => {
    setIsHover(false);
  };

  const imageSize = useMemo(() => {
    if (openHover) {
      if (desktop) {
        return {
          width: 256,
          height: 256,
        };
      }
      return {
        width: 202,
        height: 202,
      };
    }
    if (imageType === 'lifestyle_image') {
      if (desktop) {
        return {
          width: 256,
          height: 256,
        };
      }
      return {
        width: 202,
        height: 202,
      };
    }
    if (desktop) {
      return {
        width: 256,
        height: 256,
      };
    }
    return {
      width: 202,
      height: 202,
    };
  }, [imageType, openHover, desktop]);

  const imageRealSize = useMemo(() => {
    if (length) {
      return {
        width: length,
        height: length,
      };
    } else {
      return imageSize;
    }
    return imageSize;
  }, [imageSize, length]);

  const usualImageSrc = useMemo(() => {
    if (openHover) {
      return baseChangeColor;
    }
    if (imageType === 'lifestyle_image') {
      return lifestyle !== '' ? lifestyle : baseChangeColor;
    }
    return baseChangeColor;
  }, [imageType, lifestyle, baseChangeColor, openHover]);

  const handleAddToCart = async () => {
    if (productType === 'bundle') {
      handleViewDetails();
      return;
    }
    if (waitingAddToCart || addToCartSuccess) {
      return;
    }
    setWaitingAddToCart(true);
    const data = await getProductBySKU(sku);
    if (data?.variants?.[0]?.id) {
      const addToOrderData = {
        number: currentOrderData?.number || '',
        quantity: data?.min_sale_qty || 1,
        variant_id: Number(variantId),
      };
      try {
        const res = await addToOrder(addToOrderData);
        if (res !== null) {
          await dispatch(setOrder(res));
          setWaitingAddToCart(false);
          setAddToCartSuccess(true);
          dispatch(
            EVENT_ADD_TO_CART({
              trackedProduct: getProductNeedTracking(res?.line_items?.[res?.line_items?.length - 1]),
              cartLineItems: currentOrderData?.line_items || [],
              qtyIncrements: data?.min_sale_qty || 1,
              itemTotal: toPrice(String(price), true),
              atcType: '1click',
            })
          );
          onModalOpen?.(true, 'success', undefined);
        }
      } catch (error) {
        // 提取错误详情
        let errorDetail = 'Unable to add item to cart';
        setWaitingAddToCart(false);
        if (error instanceof Error) {
          try {
            // 尝试从错误消息中解析 JSON
            const errorMessage = error.message;
            const jsonMatch = errorMessage.match(/\{.*\}/);
            if (jsonMatch) {
              const errorData = JSON.parse(jsonMatch[0]);
              if (errorData.errors && errorData.errors.length > 0) {
                errorDetail = errorData.errors[0].detail || errorDetail;
              }
            }
          } catch (parseError) {
            console.warn('Failed to parse error detail:', parseError);
          }
        }
        setWaitingAddToCart(false);
        onModalOpen?.(true, 'error', errorDetail);
      }
    }
  };

  const handleClickWishlist = async () => {
    if (isSelected) {
      await handleRemoveFromWishlist();
    } else {
      await handleAddToWishlist();
    }
  };

  const handleAddToWishlist = async () => {
    // const addToWishlistData = {
    //   id: Number(variantId),
    // };
    const res = await dispatch(addWishlist.initiate(variantId));
    // const res = await addToWishlist(addToWishlistData);
    if (res) {
      setIsSelected(true);
      dispatch(updateWishlistActionRecord('add'));
      const newWishList = [...wishList];
      if (!newWishList.find((item) => item.id === Number(variantId))) {
        newWishList.push({ id: Number(variantId) });
      }
      await dispatch(setWishList(newWishList));
      onToastOpen?.('add');
      dispatch(
        EVENT_ADD_TO_WISHLIST({
          variant: {
            name: spuName,
            sku: sku,
            price: toPrice(String(price), true),
          },
        })
      );
    }
  };

  const handleViewDetails = () => {
    window.location.href = url;
  };

  const handleRemoveFromWishlist = async () => {
    if (inWishlistPage) {
      onToastOpen?.('temp_remove', {
        id: Number(variantId),
        name: spuName,
      });
      return;
    }
    await dispatch(deleteWishlist.initiate(variantId));
    onToastOpen?.('remove');
    setIsSelected(false);
  };

  const handleProductClick = (e: React.MouseEvent<HTMLAnchorElement>, url: string) => {
    e.preventDefault();
    if (!inWishlistPage) {
      dispatch(
        EVENT_STORYBLOK({ action: 'detailed_product_listing_click', label: name, method: document?.title || '' })
      );
    }
    if (slotId && onClickSuccess && !inWishlistPage) {
      onClickSuccess({
        slotId,
      });
    }
    window.setTimeout(() => {
      window.location.href = url;
    }, 1000);
  };

  const renderImagePart = () => {
    if (desktop) {
      return (
        <Stack
          sx={(theme) => ({
            position: 'relative',
            width: imageRealSize.width,
            height: imageRealSize.height,
            backgroundColor: bgColor || theme.palette.brand.warmLinen[200],
            justifyContent: 'flex-end',
            mb: '16px',
          })}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <FortressImage
            src={usualImageSrc}
            alt={name}
            ratio={1}
            // ratio={imageRealSize.width / imageRealSize.height}
            objectFit="contain"
            imageWidth={imageRealSize.width}
            imageHeight={imageRealSize.height}
            sx={{
              '--AspectRatio-paddingBottom': 0,
            }}
          />
          {openHover && lifestyle !== '' && (
            <FortressImage
              src={lifestyle}
              alt={name}
              ratio={1}
              objectFit="contain"
              imageWidth={imageRealSize.width}
              imageHeight={imageRealSize.height}
              sx={{
                position: 'absolute',
                opacity: isHover ? 1 : 0,
                transition: 'opacity 0.3s ease-in-out',
                '--AspectRatio-paddingBottom': 0,
              }}
            />
          )}
          {badges.length > 0 && badges[0] !== '' && (
            <Typography
              level="caption2"
              sx={(theme) => ({
                position: 'absolute',
                top: theme.spacing(4),
                left: theme.spacing(4),
                backgroundColor: theme.palette.brand.terracotta[500],
                color: theme.palette.brand.warmLinen[500],
                padding: theme.spacing(1),
                fontSize: 12,
              })}
            >
              {badges[0]}
            </Typography>
          )}
        </Stack>
      );
    }
    if (lifestyle !== '' && openHover) {
      return (
        <Stack
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          sx={(theme) => ({
            position: 'relative',
            width: imageSize.width,
            height: imageSize.height,
            overflow: 'hidden',
            mb: theme.spacing(4),
            '.slick-track': {
              display: 'flex',
            },
            '.slick-list': {
              overflow: 'hidden',
              touchAction: 'pan-y pinch-zoom',
            },
            '.slick-slide': {
              div: {
                position: 'relative',
              },
            },
            '.slick-dots': {
              position: 'absolute',
              bottom: '8px',
              width: '28px',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex !important',
              padding: 0,
              margin: 0,
              '.slick-active': {
                backgroundColor: (theme) => theme.palette.brand.sage[500],
              },
              li: {
                minWidth: '8px',
                minHeight: '8px',
                borderRadius: '50%',
                backgroundColor: '#CCC',
                'list-style-type': 'none',
                marginRight: '8px',
              },
              button: {
                display: 'none',
              },
            },
          })}
        >
          <Slider arrows={false} dots={true} slidesToShow={1} slidesToScroll={1} infinite={false}>
            <Stack
              sx={{
                position: 'absolute',
                zIndex: 1,
                left: 0,
                top: 0,
                width: imageRealSize.width + 2,
                height: imageRealSize.height,
                overflow: 'hidden',
              }}
            >
              <FortressImage
                src={usualImageSrc}
                alt={name}
                ratio={imageRealSize.width / imageRealSize.height}
                objectFit="contain"
                imageWidth={imageRealSize.width}
                imageHeight={imageRealSize.height}
                sx={{
                  '--AspectRatio-paddingBottom': 0,
                }}
              />
            </Stack>
            <Stack
              sx={{
                position: 'absolute',
                zIndex: 2,
                left: 0,
                top: 0,
                width: imageRealSize.width,
                height: imageRealSize.height,
                overflow: 'hidden',
              }}
            >
              <FortressImage
                src={lifestyle}
                alt={name}
                ratio={imageRealSize.width / imageRealSize.height}
                objectFit="contain"
                imageWidth={imageRealSize.width}
                imageHeight={imageRealSize.height}
                sx={{
                  '--AspectRatio-paddingBottom': 0,
                }}
              />
            </Stack>
          </Slider>
          {badges.length > 0 && badges[0] !== '' && (
            <Typography
              level="caption2"
              sx={(theme) => ({
                position: 'absolute',
                top: theme.spacing(4),
                left: theme.spacing(4),
                backgroundColor: theme.palette.brand.terracotta[500],
                color: theme.palette.brand.warmLinen[500],
                padding: theme.spacing(1),
                fontSize: 12,
              })}
            >
              {badges[0]}
            </Typography>
          )}
        </Stack>
      );
    }
    return (
      <Stack sx={{ position: 'relative', height: imageRealSize.height }} justifyContent="flex-end">
        <FortressImage
          src={usualImageSrc}
          alt={name}
          ratio={imageRealSize.width / imageRealSize.height}
          objectFit="contain"
          imageWidth={imageRealSize.width}
          imageHeight={imageRealSize.height}
          sx={{
            '--AspectRatio-paddingBottom': 0,
          }}
        />
        {badges.length > 0 && badges[0] !== '' && (
          <Typography
            sx={(theme) => ({
              position: 'absolute',
              top: theme.spacing(4),
              left: theme.spacing(4),
              backgroundColor: theme.palette.brand.terracotta[500],
              color: theme.palette.brand.warmLinen[500],
              padding: theme.spacing(1),
              fontSize: 12,
            })}
            level="caption2"
          >
            {badges[0]}
          </Typography>
        )}
      </Stack>
    );
  };

  const renderPricePart = () => {
    if (showLineThroughPrice && salePrice !== '') {
      if (Number(price) === Number(salePrice)) {
        return (
          <Stack
            sx={(theme) => ({
              flexDirection: 'row',
              alignItems: 'center',
              mb: '16px',
            })}
          >
            <Typography
              sx={(theme) => ({
                color: theme.palette.brand.maroonVelvet[500],
                fontSize: 20,
                fontFamily: `var(--font-aime, "Aime"),var(--fortress-fontFamily-fallback)`,
                ...(!desktop && {
                  fontSize: '18px !important',
                }),
              })}
            >
              {toPrice(String(price), true)}
            </Typography>
          </Stack>
        );
      } else {
        return (
          <Stack
            sx={(theme) => ({
              flexDirection: 'row',
              alignItems: 'center',
              mb: '16px',
            })}
          >
            <Typography
              level="h5"
              sx={(theme) => ({
                color: theme.palette.brand.terracotta[500],
                fontFamily: `var(--font-aime, "Aime"),var(--fortress-fontFamily-fallback)`,
              })}
            >
              {toPrice(salePrice, true)}
            </Typography>
            {showLineThroughPrice && (
              <Typography
                level="h5"
                sx={(theme) => ({
                  color: theme.palette.brand.mono[500],
                  textDecoration: 'line-through',
                  ml: '12px',
                  fontFamily: `var(--font-aime, "Aime"),var(--fortress-fontFamily-fallback)`,
                })}
              >
                {toPrice(String(price), true)}
              </Typography>
            )}
          </Stack>
        );
      }
    } else {
      return (
        <Stack
          sx={(theme) => ({
            flexDirection: 'row',
            alignItems: 'center',
            mb: '16px',
          })}
        >
          <Typography
            level="h5"
            sx={(theme) => ({
              color: theme.palette.brand.maroonVelvet[500],
              fontFamily: `var(--font-aime, "Aime"),var(--fortress-fontFamily-fallback)`,
            })}
          >
            {toPrice(String(price), true)}
          </Typography>
        </Stack>
      );
    }
  };

  return (
    <Stack
      sx={(theme) => ({
        justifyContent: 'space-between',
        height: '100%',
        ...(needMargin && {
          marginRight: desktop ? '24px' : '12px',
        }),
        ...(length && {
          marginBottom: desktop ? '40px' : '32px',
        }),
        a: {
          textDecoration: 'none',
        },
      })}
    >
      <CustomLink href={url} onClick={(e) => handleProductClick(e, url)}>
        {renderImagePart()}
        <Typography
          level="h5"
          sx={(theme) => ({
            mb: '8px',
            color: theme.palette.brand.maroonVelvet[500],
            ...(!desktop && {
              mb: '4px',
            }),
          })}
        >
          {spuName}
        </Typography>
      </CustomLink>
      <Typography
        level="caption1"
        sx={(theme) => ({
          mb: '16px',
          ...(!desktop && {
            mb: '12px',
          }),
        })}
      >
        {productShortDescription}
      </Typography>
      {renderPricePart()}
      {applyATCAndWishlist && (
        <Stack
          sx={{
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          {productType === 'bundle' && desktop ? (
            <Button
              variant="outlined"
              onClick={handleViewDetails}
              sx={(theme) => ({
                paddingTop: theme.spacing(1),
                paddingBottom: theme.spacing(1),
                minHeight: theme.spacing(10),
                maxHeight: theme.spacing(10),
                border: `1px solid ${theme.palette.brand.maroonVelvet[500]}`,
                backgroundColor: 'transparent',
                color: theme.palette.brand.maroonVelvet[500],
                '&:hover': {
                  border: `1px solid ${theme.palette.brand.maroonVelvet[500]}`,
                  backgroundColor: '#63404B',
                  color: '#f6f3e7',
                },
                mr: theme.spacing(3),
              })}
            >
              View Details
            </Button>
          ) : (
            <IconButton
              size="md"
              aria-label="Cart Action"
              sx={(theme) => ({
                borderRadius: '50%',
                border: `1px solid ${theme.palette.brand.maroonVelvet[500]}`,
                mr: theme.spacing(3),
                backgroundColor: theme.palette.brand.warmLinen[500],
                '&:hover': {
                  border: `1px solid ${theme.palette.brand.maroonVelvet[500]}`,
                  backgroundColor: '#F9F7F3',
                },
                cursor: addToCartSuccess || waitingAddToCart ? 'default' : 'pointer',
              })}
              onClick={handleAddToCart}
            >
              {addToCartSuccess ? (
                <Check
                  sx={(theme) => ({
                    fill: theme.palette.brand.maroonVelvet[500],
                    width: '16px',
                    height: '16px',
                  })}
                />
              ) : waitingAddToCart ? (
                <Loading
                  sx={(theme) => ({
                    width: '16px',
                    height: '16px',
                    fill: theme.palette.brand.maroonVelvet[500],
                    '@keyframes rotate': {
                      from: {
                        transform: 'rotate(0deg)',
                      },
                      to: {
                        transform: 'rotate(360deg)',
                      },
                    },
                    animation: 'rotate .5s linear infinite',
                  })}
                />
              ) : (
                <ShoppingBag
                  sx={(theme) => ({
                    fill: theme.palette.brand.maroonVelvet[500],
                    width: '16px',
                    height: '16px',
                  })}
                />
              )}
            </IconButton>
          )}
          <IconButton
            size="md"
            aria-label="Wishlist Action"
            sx={(theme) => ({
              borderRadius: '50%',
              border: `1px solid ${theme.palette.brand.maroonVelvet[500]}`,
              backgroundColor: isSelected ? theme.palette.brand.maroonVelvet[500] : theme.palette.brand.warmLinen[500],
              '&:hover': {
                border: `1px solid ${theme.palette.brand.maroonVelvet[500]}`,
                backgroundColor: isSelected ? theme.palette.brand.maroonVelvet[500] : '#F9F7F3',
              },
            })}
            onClick={handleClickWishlist}
          >
            {isSelected ? (
              <Favorite
                sx={(theme) => ({
                  fill: theme.palette.brand.warmLinen[500],
                  width: '16px',
                  height: '16px',
                })}
              />
            ) : (
              <Favorite
                sx={(theme) => ({ fill: theme.palette.brand.maroonVelvet[500], width: '16px', height: '16px' })}
              />
            )}
          </IconButton>
        </Stack>
      )}
    </Stack>
  );
};

export { ProductItemDataProps, SingleProductItem };
